// Action-buttons row for the Inventory panel's Materials tab body.
//
// Lives inside the tab body (post 2026-06-02 polish) so the buttons
// are visually attached to the materials list they affect — parallel
// to how the Receive lines tab body owns its own `+ Add Line`
// affordance. The orchestrator owns the modal and bulk-import state;
// this component is a presentational button row plus the Export CSV
// download and the Bulk-import dropdown menu (which folds the prior
// Bulk import + Template buttons into one trigger).
import { useEffect, useRef, useState } from "react";
import { downloadCsv, materialsToCsv } from "@/lib/manufacturing/inventoryCsvExport";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import {
  ghostBtnClass,
  mfgAccentBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  filtered: MfgMaterial[];
  gateEnabled: boolean;
  busy: boolean;
  onCreate: () => void;
  onBulkImport: () => void;
  onTemplate: () => void;
  onInventoryCount: () => void;
  onSdsBook: () => void;
  onToggleDuplicates: () => void;
  duplicatesOpen: boolean;
};

export function ManufacturingMaterialsActions({
  filtered,
  gateEnabled,
  busy,
  onCreate,
  onBulkImport,
  onTemplate,
  onInventoryCount,
  onSdsBook,
  onToggleDuplicates,
  duplicatesOpen,
}: Props) {
  function handleExport() {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`mfg-inventory-${stamp}.csv`, materialsToCsv(filtered));
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" className={mfgPrimaryBtnClass} onClick={onCreate} disabled={busy || !gateEnabled}>
        + New Material
      </button>
      <button
        type="button"
        className={mfgAccentBtnClass}
        onClick={onInventoryCount}
        disabled={busy || !gateEnabled}
        title="Open Production → Inventory count (plant materials session)"
      >
        Inventory count
      </button>
      <BulkImportMenu
        disabled={busy || !gateEnabled}
        templateDisabled={!gateEnabled}
        onUpload={onBulkImport}
        onTemplate={onTemplate}
      />
      <button
        type="button"
        className={ghostBtnClass}
        onClick={onSdsBook}
        disabled={busy || !gateEnabled}
        title="Export plant SDS book PDF"
      >
        SDS book
      </button>
      <button
        type="button"
        className={ghostBtnClass}
        onClick={handleExport}
        disabled={busy || !gateEnabled || filtered.length === 0}
      >
        Export CSV
      </button>
      <button
        type="button"
        className={ghostBtnClass}
        onClick={onToggleDuplicates}
        disabled={!gateEnabled || filtered.length === 0}
        aria-pressed={duplicatesOpen}
        title="Group materials whose primary description matches"
      >
        {duplicatesOpen ? "Hide duplicates" : "Find duplicates"}
      </button>
    </div>
  );
}

type BulkImportMenuProps = {
  disabled: boolean;
  templateDisabled: boolean;
  onUpload: () => void;
  onTemplate: () => void;
};

// Inline dropdown for the Bulk import action set. Folds two prior
// adjacent buttons (Bulk import + Template) into one trigger so the
// actions row stays uncluttered. Implementation is local (~50 lines)
// rather than reaching for `useRailPopover` because the rail-popover
// hook handles fixed-position right/below placement keyed off the
// sidebar; here we just want a list anchored below the trigger.
//
// Dev-sync note 0def214f — the Template option still routes through
// the orchestrator-owned "Download Inventory Template CSV?" confirm
// modal; only the trigger location moved.
function BulkImportMenu({ disabled, templateDisabled, onUpload, onTemplate }: BulkImportMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled && templateDisabled}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Bulk import <span aria-hidden className="ml-1 text-[10px]">▾</span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-100 dark:hover:bg-zinc-800"
            onClick={() => {
              setOpen(false);
              onUpload();
            }}
            disabled={disabled}
          >
            <span className="font-medium">Upload CSV…</span>
            <span className="text-[11px] text-gray-500 dark:text-zinc-400">
              Bulk-import materials from a CSV file.
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-100 dark:hover:bg-zinc-800"
            onClick={() => {
              setOpen(false);
              onTemplate();
            }}
            disabled={templateDisabled}
          >
            <span className="font-medium">Download template…</span>
            <span className="text-[11px] text-gray-500 dark:text-zinc-400">
              Get a CSV with sample rows showing the column shape.
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
